// Sector-based consent templates
// 5 sectors with 5 templates each = 25 total templates

export const SECTOR_TEMPLATES = {
  bank: {
    sector: 'Bank',
    color: '#0070f3',
    icon: '🏦',
    templates: [
      {
        id: 'bank_account_opening',
        name: 'Account Opening Consent',
        description: 'Essential consent for new account creation and KYC verification',
        mandatory: true,
        purpose: 'To establish and maintain your banking account with complete KYC compliance',
        content: `
          <h3>Account Opening Consent</h3>
          <p><strong>Purpose:</strong> This consent authorizes us to collect, process, and store your personal information for account opening and KYC (Know Your Customer) verification.</p>
          <p><strong>Information Collected:</strong></p>
          <ul>
            <li>Full name and date of birth</li>
            <li>Residential address and PAN details</li>
            <li>Income information and employment details</li>
            <li>Bank account details and transaction history</li>
            <li>Government-issued identification</li>
          </ul>
          <p><strong>Data Processing:</strong> Your information will be processed in compliance with RBI guidelines and PMLA requirements. Data will be retained as per regulatory requirements.</p>
          <p><strong>Your Rights:</strong> You can update or modify your information at any time through our mobile app or visiting a branch.</p>
        `
      },
      {
        id: 'bank_kyc_data',
        name: 'KYC Data Processing',
        description: 'Consent for Know Your Customer verification and regulatory compliance',
        mandatory: true,
        purpose: 'To comply with regulatory requirements and verify your identity',
        content: `
          <h3>KYC Data Processing Consent</h3>
          <p><strong>Regulatory Requirement:</strong> KYC verification is mandatory under RBI guidelines to prevent financial fraud and money laundering.</p>
          <p><strong>Data Usage:</strong></p>
          <ul>
            <li>Identity verification through UIDAI or other government databases</li>
            <li>Address verification through postal database</li>
            <li>PEP (Politically Exposed Person) screening</li>
            <li>Negative list checking with government agencies</li>
          </ul>
          <p><strong>Third-Party Sharing:</strong> Information may be shared with RBI, CIBIL, and other regulatory authorities as required by law.</p>
          <p><strong>Duration:</strong> KYC data will be maintained throughout your relationship with us.</p>
        `
      },
      {
        id: 'bank_personal_data',
        name: 'Personal Data Processing',
        description: 'Consent for processing personal information for account management',
        mandatory: true,
        purpose: 'To manage your account and provide banking services',
        content: `
          <h3>Personal Data Processing Consent</h3>
          <p><strong>Data Processing Activities:</strong></p>
          <ul>
            <li>Transaction monitoring and fraud detection</li>
            <li>Account maintenance and customer service</li>
            <li>Credit history reporting to CIBIL</li>
            <li>Communication regarding account updates and offers</li>
          </ul>
          <p><strong>Security Measures:</strong> We employ industry-standard encryption and security protocols to protect your data.</p>
          <p><strong>Retention Policy:</strong> Personal data is retained for 7 years after account closure.</p>
        `
      },
      {
        id: 'bank_credit_bureau',
        name: 'Credit Bureau Reporting',
        description: 'Consent for sharing credit information with credit bureaus',
        mandatory: false,
        purpose: 'To build and maintain your credit profile',
        content: `
          <h3>Credit Bureau Reporting Consent</h3>
          <p><strong>Credit Reporting:</strong> We will report your account payment history to CIBIL, Equifax, Experian, and other credit bureaus.</p>
          <p><strong>Information Shared:</strong></p>
          <ul>
            <li>Account opening and closing dates</li>
            <li>Monthly payment history</li>
            <li>Outstanding balance and credit limit</li>
            <li>Loan/credit facility details</li>
          </ul>
          <p><strong>Impact on Credit Score:</strong> Your credit behavior will affect your credit score which impacts future credit applications.</p>
          <p><strong>Your Rights:</strong> You can check your credit report and dispute inaccuracies directly with the credit bureaus.</p>
        `
      },
      {
        id: 'bank_digital_banking',
        name: 'Internet & Mobile Banking',
        description: 'Consent for digital banking services and online transactions',
        mandatory: false,
        purpose: 'To provide digital banking and transaction services',
        content: `
          <h3>Internet & Mobile Banking Consent</h3>
          <p><strong>Digital Services:</strong> This consent enables you to use our internet banking portal and mobile applications.</p>
          <p><strong>Services Include:</strong></p>
          <ul>
            <li>Fund transfers and bill payments</li>
            <li>Check deposits through mobile app</li>
            <li>Loan applications and account management</li>
            <li>Investment portfolio monitoring</li>
          </ul>
          <p><strong>Security Acknowledgment:</strong> You acknowledge the inherent risks of digital transactions and agree to maintain your credentials securely.</p>
          <p><strong>Device Information:</strong> We will collect device ID, IP address, and usage patterns for security and improvement purposes.</p>
        `
      }
    ]
  },
  insurance: {
    sector: 'Insurance',
    color: '#00b894',
    icon: '🛡️',
    templates: [
      {
        id: 'insurance_policy_enrollment',
        name: 'Policy Enrollment Consent',
        description: 'Consent required for insurance policy enrollment and activation',
        mandatory: true,
        purpose: 'To create and activate your insurance policy',
        content: `
          <h3>Policy Enrollment Consent</h3>
          <p><strong>Purpose:</strong> This consent authorizes us to process your insurance policy application and create your insurance account.</p>
          <p><strong>Information Collection:</strong></p>
          <ul>
            <li>Personal identification details</li>
            <li>Age, health status, and occupation</li>
            <li>Lifestyle habits and medical history</li>
            <li>Beneficiary information</li>
            <li>Payment method details</li>
          </ul>
          <p><strong>Underwriting Process:</strong> Your information will be used for risk assessment and policy underwriting.</p>
          <p><strong>Policy Duration:</strong> Data will be retained throughout the policy period and as per regulatory requirements.</p>
        `
      },
      {
        id: 'insurance_health_data',
        name: 'Health Data Collection',
        description: 'Consent for collecting and processing health information',
        mandatory: true,
        purpose: 'To assess health risks and determine insurance coverage',
        content: `
          <h3>Health Data Collection Consent</h3>
          <p><strong>Health Information Collected:</strong></p>
          <ul>
            <li>Medical history and current health conditions</li>
            <li>Prescription medications and allergies</li>
            <li>Hospitalizations and surgeries</li>
            <li>Family medical history</li>
            <li>Lifestyle risk factors (smoking, alcohol consumption)</li>
          </ul>
          <p><strong>Data Source:</strong> Information may be obtained from you or with your consent from healthcare providers.</p>
          <p><strong>Confidentiality:</strong> All health information is treated as highly confidential and protected under HIPAA guidelines.</p>
          <p><strong>Use Restriction:</strong> Health data will only be used for underwriting and claims processing.</p>
        `
      },
      {
        id: 'insurance_claims_processing',
        name: 'Claim Processing Authorization',
        description: 'Consent for processing insurance claims and verification',
        mandatory: true,
        purpose: 'To process and verify insurance claims',
        content: `
          <h3>Claim Processing Authorization</h3>
          <p><strong>Claims Handling:</strong> This consent authorizes us to investigate and process your insurance claims.</p>
          <p><strong>Investigation Process:</strong></p>
          <ul>
            <li>Medical report verification from hospitals</li>
            <li>Communication with healthcare providers</li>
            <li>Fraud investigation and verification</li>
            <li>Third-party verification as needed</li>
          </ul>
          <p><strong>Claim Settlement:</strong> Claims will be settled within 30 days of receiving complete documentation.</p>
          <p><strong>Information Sharing:</strong> We may share claim information with reinsurers and regulatory authorities.</p>
        `
      },
      {
        id: 'insurance_marketing',
        name: 'Marketing Communications',
        description: 'Consent for receiving marketing communications and product updates',
        mandatory: false,
        purpose: 'To send you relevant offers, product information, and renewal reminders',
        content: `
          <h3>Marketing Communications Consent</h3>
          <p><strong>Communication Channels:</strong> We will communicate through:</p>
          <ul>
            <li>Email newsletters and product updates</li>
            <li>SMS alerts for policy renewals and claim updates</li>
            <li>Telephone calls for service updates</li>
            <li>Push notifications through mobile app</li>
          </ul>
          <p><strong>Frequency:</strong> Communications will be sent monthly or as needed for important policy information.</p>
          <p><strong>Opt-Out:</strong> You can unsubscribe from marketing communications anytime through your policy dashboard.</p>
          <p><strong>Important Notices:</strong> You will continue to receive policy-critical communications regardless of this consent.</p>
        `
      },
      {
        id: 'insurance_data_sharing',
        name: 'Data Sharing with Partners',
        description: 'Consent for sharing information with insurance partners and agencies',
        mandatory: false,
        purpose: 'To coordinate coverage and verify information with other insurers',
        content: `
          <h3>Data Sharing with Partners Consent</h3>
          <p><strong>Third Parties:</strong> Your data may be shared with:</p>
          <ul>
            <li>Medical networks and healthcare providers</li>
            <li>Pharmacy networks for claim processing</li>
            <li>Other insurance companies for coordination of benefits</li>
            <li>Government and regulatory agencies</li>
            <li>Reinsurance companies</li>
          </ul>
          <p><strong>Data Protection:</strong> All partners are bound by strict confidentiality agreements and data protection laws.</p>
          <p><strong>Purpose:</strong> Data sharing is only for claim settlement, fraud prevention, and regulatory compliance.</p>
        `
      }
    ]
  },
  hotel: {
    sector: 'Hotel',
    color: '#0984e3',
    icon: '🏨',
    templates: [
      {
        id: 'hotel_guest_registration',
        name: 'Guest Registration Consent',
        description: 'Consent for collecting guest information during hotel check-in',
        mandatory: true,
        purpose: 'To complete guest registration and provide hotel services',
        content: `
          <h3>Guest Registration Consent</h3>
          <p><strong>Information Collected:</strong></p>
          <ul>
            <li>Full name and contact details</li>
            <li>Identification document (Passport/ID number)</li>
            <li>Billing and payment information</li>
            <li>Emergency contact details</li>
            <li>Special requests and dietary requirements</li>
            <li>Vehicle registration (if applicable)</li>
          </ul>
          <p><strong>Purpose:</strong> This information is collected to provide you with personalized hotel services and maintain security.</p>
          <p><strong>Duration:</strong> Information is retained for 3 years as per hospitality regulations.</p>
          <p><strong>Confidentiality:</strong> All guest information is treated as confidential and protected under data protection laws.</p>
        `
      },
      {
        id: 'hotel_photography_privacy',
        name: 'Photography & Privacy Policy',
        description: 'Consent for hotel photography and video surveillance',
        mandatory: false,
        purpose: 'To enhance security and enable event photography services',
        content: `
          <h3>Photography & Privacy Consent</h3>
          <p><strong>CCTV Surveillance:</strong> Hotel premises are monitored with CCTV for security purposes.</p>
          <p><strong>Photography:</strong></p>
          <ul>
            <li>Professional photography during events and conferences</li>
            <li>Photos may be used in hotel marketing materials</li>
            <li>Photos may be shared on social media unless opted out</li>
          </ul>
          <p><strong>Video Recording:</strong> Guest images may be recorded for security analysis and staff training.</p>
          <p><strong>Opt-Out:</strong> You can request opt-out from photography by notifying the front desk.</p>
        `
      },
      {
        id: 'hotel_payment_billing',
        name: 'Payment & Billing Consent',
        description: 'Consent for processing payments and billing information',
        mandatory: true,
        purpose: 'To process room charges and additional service payments',
        content: `
          <h3>Payment & Billing Consent</h3>
          <p><strong>Payment Processing:</strong> This consent authorizes us to process:</p>
          <ul>
            <li>Room booking and accommodation charges</li>
            <li>Food and beverage purchases</li>
            <li>Spa and activity services</li>
            <li>Late checkout fees and other incidental charges</li>
          </ul>
          <p><strong>Credit Card Information:</strong> Payment card details are encrypted and processed securely via certified payment gateways.</p>
          <p><strong>Billing Cycle:</strong> Charges are billed at checkout or to your designated payment method.</p>
          <p><strong>Dispute Resolution:</strong> Any billing discrepancies must be reported within 30 days of checkout.</p>
        `
      },
      {
        id: 'hotel_feedback_survey',
        name: 'Guest Feedback & Surveys',
        description: 'Consent for collecting guest feedback and conducting surveys',
        mandatory: false,
        purpose: 'To improve hotel services and collect quality feedback',
        content: `
          <h3>Guest Feedback & Survey Consent</h3>
          <p><strong>Feedback Collection:</strong> We collect feedback through:</p>
          <ul>
            <li>Post-stay email surveys</li>
            <li>In-room feedback forms</li>
            <li>Mobile app reviews</li>
            <li>Third-party review platforms</li>
          </ul>
          <p><strong>Frequency:</strong> Surveys are sent once per stay within 24 hours of checkout.</p>
          <p><strong>Incentives:</strong> Participation may earn loyalty points or special discounts.</p>
          <p><strong>Data Usage:</strong> Feedback is analyzed to improve hotel operations and staff training.</p>
        `
      },
      {
        id: 'hotel_loyalty_program',
        name: 'Loyalty Program Enrollment',
        description: 'Consent for loyalty program participation and benefits',
        mandatory: false,
        purpose: 'To enroll you in our loyalty rewards program',
        content: `
          <h3>Loyalty Program Enrollment Consent</h3>
          <p><strong>Program Benefits:</strong> Our loyalty program offers:</p>
          <ul>
            <li>Points accumulation on room stays</li>
            <li>Exclusive member discounts</li>
            <li>Priority booking and room upgrades</li>
            <li>Access to special events and experiences</li>
            <li>Birthday and anniversary rewards</li>
          </ul>
          <p><strong>Information Tracking:</strong> Your stays and spending patterns are tracked to personalize offers.</p>
          <p><strong>Points Expiry:</strong> Loyalty points expire after 3 years of inactivity.</p>
          <p><strong>Program Modifications:</strong> Program terms may be updated with 30 days' notice.</p>
        `
      }
    ]
  },
  financial_corporate: {
    sector: 'Financial Corporate',
    color: '#fdcb6e',
    icon: '💼',
    templates: [
      {
        id: 'corp_investment_account',
        name: 'Investment Account Opening',
        description: 'Consent for opening investment and trading accounts',
        mandatory: true,
        purpose: 'To establish and manage your investment account',
        content: `
          <h3>Investment Account Opening Consent</h3>
          <p><strong>Account Information:</strong> This consent authorizes us to collect and process:</p>
          <ul>
            <li>Personal and financial information</li>
            <li>Investment experience and goals</li>
            <li>Annual income and net worth details</li>
            <li>Employment and business information</li>
            <li>Bank account details for transfers</li>
          </ul>
          <p><strong>Regulatory Compliance:</strong> Information is used for KYC verification and compliance with SEBI guidelines.</p>
          <p><strong>Account Management:</strong> You can access and modify account information through our online portal.</p>
          <p><strong>Regulatory Requirement:</strong> This is a mandatory document for account activation.</p>
        `
      },
      {
        id: 'corp_risk_disclosure',
        name: 'Risk Disclosure & Suitability',
        description: 'Consent acknowledging investment risks and product suitability',
        mandatory: true,
        purpose: 'To confirm your understanding of investment risks',
        content: `
          <h3>Risk Disclosure & Suitability Consent</h3>
          <p><strong>Investment Risks Acknowledged:</strong></p>
          <ul>
            <li>Market risk and volatility</li>
            <li>Liquidity risk for certain instruments</li>
            <li>Credit and counterparty risk</li>
            <li>Currency risk for international investments</li>
            <li>Regulatory and geopolitical risks</li>
          </ul>
          <p><strong>Suitability Confirmation:</strong> We confirm that recommended products are suitable for your investment profile.</p>
          <p><strong>Performance Disclaimer:</strong> Past performance is not indicative of future results.</p>
          <p><strong>Review Frequency:</strong> We will review suitability annually or when your financial situation changes.</p>
        `
      },
      {
        id: 'corp_financial_data',
        name: 'Financial Data & Tax Information',
        description: 'Consent for processing financial and tax-related information',
        mandatory: true,
        purpose: 'To manage investments and handle tax reporting',
        content: `
          <h3>Financial Data & Tax Information Consent</h3>
          <p><strong>Financial Information Collected:</strong></p>
          <ul>
            <li>Income sources and tax brackets</li>
            <li>Capital gains and losses</li>
            <li>Dividend and interest income</li>
            <li>Tax identification numbers (PAN)</li>
            <li>Tax filing status and obligations</li>
          </ul>
          <p><strong>Tax Reporting:</strong> We will generate annual tax statements and reports for your filing.</p>
          <p><strong>TDS Processing:</strong> Tax deductions at source will be processed as per tax regulations.</p>
          <p><strong>Regulatory Reporting:</strong> Information may be reported to income tax authorities as required.</p>
        `
      },
      {
        id: 'corp_automated_trading',
        name: 'Automated Trading & Algorithms',
        description: 'Consent for using automated trading strategies and algorithms',
        mandatory: false,
        purpose: 'To enable automated trading based on predefined strategies',
        content: `
          <h3>Automated Trading & Algorithms Consent</h3>
          <p><strong>Automated Services:</strong> This enables:</p>
          <ul>
            <li>Algorithmic trading based on technical indicators</li>
            <li>Automated portfolio rebalancing</li>
            <li>Dividend reinvestment programs</li>
            <li>Systematic investment plans (SIP)</li>
          </ul>
          <p><strong>Algorithm Disclosure:</strong> Trading algorithms are regularly reviewed and tested for performance.</p>
          <p><strong>Circuit Breakers:</strong> Automatic safeguards prevent excessive losses or volatility.</p>
          <p><strong>Monitoring:</strong> All automated trades are monitored and can be overridden manually.</p>
        `
      },
      {
        id: 'corp_financial_advisory',
        name: 'Financial Advisory Services',
        description: 'Consent for receiving financial advice and recommendations',
        mandatory: false,
        purpose: 'To provide personalized financial advisory services',
        content: `
          <h3>Financial Advisory Services Consent</h3>
          <p><strong>Advisory Services Include:</strong></p>
          <ul>
            <li>Portfolio allocation recommendations</li>
            <li>Retirement planning assistance</li>
            <li>Tax optimization strategies</li>
            <li>Risk assessment and management</li>
            <li>Regular portfolio reviews</li>
          </ul>
          <p><strong>Advisor Relationship:</strong> Your dedicated financial advisor will be assigned for ongoing support.</p>
          <p><strong>Conflicts of Interest:</strong> We disclose any conflicts of interest related to product recommendations.</p>
          <p><strong>Fee Structure:</strong> Advisory fees will be clearly communicated and may vary based on service complexity.</p>
        `
      }
    ]
  },
  other: {
    sector: 'General / Other',
    color: '#6c5ce7',
    icon: '📋',
    templates: [
      {
        id: 'general_personal_data',
        name: 'Personal Data Processing',
        description: 'Consent for general personal data collection and processing',
        mandatory: true,
        purpose: 'To collect and process your personal information for service delivery',
        content: `
          <h3>Personal Data Processing Consent</h3>
          <p><strong>Data Collection:</strong> We collect the following information:</p>
          <ul>
            <li>Name, email address, and contact details</li>
            <li>Age and basic demographic information</li>
            <li>User account preferences and settings</li>
            <li>Service usage patterns and history</li>
            <li>Device and IP address information</li>
          </ul>
          <p><strong>Purpose of Processing:</strong> To deliver and improve our services, respond to inquiries, and maintain service quality.</p>
          <p><strong>Data Retention:</strong> Personal data is retained only as long as necessary to provide services.</p>
          <p><strong>Data Protection:</strong> All data is protected using industry-standard security measures.</p>
        `
      },
      {
        id: 'general_communications',
        name: 'Communication & Notifications',
        description: 'Consent for communications including emails, SMS, and notifications',
        mandatory: false,
        purpose: 'To send you important updates, notifications, and service information',
        content: `
          <h3>Communication & Notifications Consent</h3>
          <p><strong>Communication Types:</strong> We will communicate through:</p>
          <ul>
            <li>Email for account updates and service information</li>
            <li>SMS alerts for time-sensitive notifications</li>
            <li>Push notifications through mobile applications</li>
            <li>Telephone calls for customer service</li>
            <li>In-app notifications and messaging</li>
          </ul>
          <p><strong>Opt-Out Options:</strong> You can manage communication preferences in account settings.</p>
          <p><strong>Critical Notifications:</strong> Certain notifications cannot be opted out as they are legally required.</p>
          <p><strong>Frequency:</strong> We limit communications to necessary updates and respect your preferences.</p>
        `
      },
      {
        id: 'general_terms_conditions',
        name: 'Terms & Conditions Agreement',
        description: 'Consent to accept terms and conditions of service usage',
        mandatory: true,
        purpose: 'To establish the legal framework for using our services',
        content: `
          <h3>Terms & Conditions Consent</h3>
          <p><strong>Service Agreement:</strong> By using our services, you agree to be bound by our terms and conditions.</p>
          <p><strong>Service Provisions:</strong></p>
          <ul>
            <li>Description and scope of services provided</li>
            <li>User responsibilities and obligations</li>
            <li>Limitations of liability</li>
            <li>Dispute resolution procedures</li>
            <li>Service modifications and discontinuation rights</li>
          </ul>
          <p><strong>User Conduct:</strong> You agree not to engage in prohibited activities including fraud, harassment, or illegal activities.</p>
          <p><strong>Term Duration:</strong> This agreement remains effective until service termination.</p>
        `
      },
      {
        id: 'general_marketing_promotions',
        name: 'Marketing & Promotional Offers',
        description: 'Consent for receiving marketing materials and promotional offers',
        mandatory: false,
        purpose: 'To send you promotional offers and marketing information',
        content: `
          <h3>Marketing & Promotional Offers Consent</h3>
          <p><strong>Marketing Communications:</strong> We will send:</p>
          <ul>
            <li>Product recommendations and promotional offers</li>
            <li>New feature announcements</li>
            <li>Seasonal campaigns and special deals</li>
            <li>Newsletter and blog updates</li>
            <li>Event invitations and webinar announcements</li>
          </ul>
          <p><strong>Personalization:</strong> Offers are personalized based on your preferences and usage history.</p>
          <p><strong>Frequency:</strong> Marketing messages are typically sent weekly or as specified in preferences.</p>
          <p><strong>Unsubscribe:</strong> You can unsubscribe from any marketing communication with a single click.</p>
        `
      },
      {
        id: 'general_cookies_tracking',
        name: 'Cookies & Tracking Technologies',
        description: 'Consent for using cookies and web tracking technologies',
        mandatory: false,
        purpose: 'To enhance user experience and track website usage patterns',
        content: `
          <h3>Cookies & Tracking Technologies Consent</h3>
          <p><strong>Cookie Usage:</strong> We use cookies for:</p>
          <ul>
            <li>Session management and login persistence</li>
            <li>User preference storage</li>
            <li>Analytics and usage tracking</li>
            <li>Advertising and retargeting</li>
            <li>Performance monitoring and optimization</li>
          </ul>
          <p><strong>Types of Cookies:</strong></p>
          <ul>
            <li>Essential: Required for basic functionality</li>
            <li>Functional: Enhance user experience</li>
            <li>Analytics: Track user behavior and patterns</li>
            <li>Marketing: Used for targeted advertising</li>
          </ul>
          <p><strong>Cookie Management:</strong> You can disable cookies through browser settings (may limit functionality).</p>
          <p><strong>Third-Party Cookies:</strong> Third-party services may place cookies for analytics and advertising purposes.</p>
        `
      }
    ]
  }
};

/**
 * Get templates for a specific sector
 * @param {string} sector - Sector identifier (bank, insurance, hotel, financial_corporate, other)
 * @returns {array} Array of templates for the sector
 */
export const getTemplatesBySector = (sector) => {
  const normalized = String(sector || '').toLowerCase().trim().replace(/\s+/g, '_');
  const sectorData = SECTOR_TEMPLATES[normalized];
  return sectorData ? sectorData.templates : [];
};

/**
 * Get all sectors with metadata
 * @returns {array} Array of sector objects with icon, color, and template count
 */
export const getAllSectors = () => {
  return Object.entries(SECTOR_TEMPLATES).map(([id, data]) => ({
    id,
    sector: data.sector,
    color: data.color,
    icon: data.icon,
    templates: data.templates
  }));
};

/**
 * Get a specific template by ID across all sectors
 * @param {string} templateId - Template identifier
 * @returns {object|null} Template object or null if not found
 */
export const getTemplateById = (templateId) => {
  for (const sectorKey in SECTOR_TEMPLATES) {
    const sector = SECTOR_TEMPLATES[sectorKey];
    const template = sector.templates.find(t => t.id === templateId);
    if (template) return template;
  }
  return null;
};

export default SECTOR_TEMPLATES;
